import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createModel, editModel } from '../services/llm';
import BasicModal from '../../../../components/action-modal';
import makeToast from '../../../../components/toaster';
import {
  buildLlmSavePayload,
  defaultLlmExtendedFields,
  llmDetailsFromModel,
  routingBandFromModelScore,
} from '../model-utils';
import { createOrEditLLMProps, llmDetailsProps } from './llm.props';
import { LLMIcon } from '../../../../assets/images';
import {
  AddProviderText,
  DescriptionText,
  ModelNameText,
  PromptCostText,
  ProviderText,
  ResponseCostText,
} from './data/constants';
import { validateNumberField, validateTextField } from '../../../../services';
import { reloadUserConfigurationsAfterLlmChange } from '../../user/services';
import Logout from '../../../../auth/protected/session-expire-modal';
import { FilterDropdown } from '../../../../components';

const MODEL_TYPE_OPTIONS = [
  'chat',
  'embedding',
  'image_generation',
  'video_generation',
  'speech_to_text',
  'text_to_speech',
  'multimodal',
];
const STATUS_OPTIONS = ['testing', 'active', 'deprecated', 'disabled'];
const HEALTH_OPTIONS = ['unknown', 'healthy', 'degraded', 'unavailable'];

function priceFieldLabel(fieldName: string): string {
  if (fieldName === 'input_price_per_million') {
    return 'Prompt price (per 1M tokens)';
  }
  if (fieldName === 'output_price_per_million') {
    return 'Response price (per 1M tokens)';
  }
  return 'Cached input price (per 1M tokens)';
}

function hasNumericInput(value: unknown): boolean {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

function emptyFormState(providerId: number): llmDetailsProps {
  return {
    provider_id: providerId,
    name: '',
    description: '',
    input_price_per_million: undefined,
    output_price_per_million: undefined,
    ...defaultLlmExtendedFields(),
  };
}

function CreateOrUpdateLLM({
  open,
  setOpen,
  modalTitle,
  modalDesc,
  dataToEdit,
  setDataUpdated,
  providers,
}: createOrEditLLMProps) {
  const defaultProviderId = providers?.[0]?.id ?? 1;
  const [llmDetails, setLLMDetails] = useState<llmDetailsProps>(emptyFormState(defaultProviderId));
  const [errorsMsgs, setErrorsMsgs] = useState({
    name: '',
    input_price_per_million: '',
    output_price_per_million: '',
    model_score: '',
  });
  const [logout, setLogout] = useState<boolean>(false);

  const previewRoutingBand =
    llmDetails.routing_band ??
    routingBandFromModelScore(llmDetails.model_score as number | undefined);

  useEffect(() => {
    if (open) {
      if (dataToEdit.id !== 0) {
        setLLMDetails(llmDetailsFromModel(dataToEdit) as llmDetailsProps);
      } else {
        setLLMDetails(emptyFormState(providers?.[0]?.id ?? defaultProviderId));
      }
    }
  }, [open, dataToEdit, providers, defaultProviderId]);

  const handleDataChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    let error = '';

    if (name === 'description' || name === 'provider_id') {
      error = '';
    } else if (
      name === 'input_price_per_million' ||
      name === 'output_price_per_million' ||
      name === 'cached_input_price_per_million'
    ) {
      error = validateNumberField(value, priceFieldLabel(name)) ?? '';
    } else if (name === 'model_score') {
      if (value !== '' && (Number(value) < 0 || Number(value) > 1)) {
        error = 'Model score must be between 0 and 1';
      }
    } else if (name === 'name') {
      error = validateTextField(value, 'Model Name') ?? '';
    }

    setErrorsMsgs((prevErrors) => ({ ...prevErrors, [name]: error }));

    setLLMDetails((prevDetails) => {
      if (name === 'provider_id') {
        return { ...prevDetails, provider_id: Number(value) || prevDetails.provider_id };
      }
      if (type === 'checkbox') {
        return { ...prevDetails, [name]: checked };
      }
      if (
        name === 'input_price_per_million' ||
        name === 'output_price_per_million' ||
        name === 'cached_input_price_per_million' ||
        name === 'model_score' ||
        name === 'context_window_tokens' ||
        name === 'max_output_tokens'
      ) {
        return { ...prevDetails, [name]: value };
      }
      return { ...prevDetails, [name]: value };
    });
  };

  const saveLLM = () => {
    const {
      name,
      input_price_per_million: inputPrice,
      output_price_per_million: outputPrice,
      model_score: modelScore,
    } = llmDetails;
    const nameError = validateTextField(name, 'Model Name');
    const promptTokenError = validateNumberField(
      String(inputPrice),
      'Prompt price (per 1M tokens)',
    );
    const responseTokenError = validateNumberField(
      String(outputPrice),
      'Response price (per 1M tokens)',
    );
    let modelScoreError = '';
    if (hasNumericInput(modelScore)) {
      const n = Number(modelScore);
      if (Number.isNaN(n) || n < 0 || n > 1) {
        modelScoreError = 'Model score must be between 0 and 1';
      }
    }
    setErrorsMsgs({
      name: nameError,
      input_price_per_million: promptTokenError,
      output_price_per_million: responseTokenError,
      model_score: modelScoreError,
    });
    if (!nameError && !promptTokenError && !responseTokenError && !modelScoreError) {
      let apiCall;
      const payload = buildLlmSavePayload(llmDetails);

      if (dataToEdit.id && dataToEdit.id !== 0) {
        apiCall = editModel(payload, dataToEdit.id);
      } else {
        apiCall = createModel(payload);
      }

      apiCall
        .then((res) => {
          if (res.status === 200 || res.status === 201) {
            setLLMDetails(emptyFormState(defaultProviderId));
            setOpen(false);
            setDataUpdated((prev: boolean) => !prev);
            reloadUserConfigurationsAfterLlmChange();
            makeToast(
              'success',
              dataToEdit?.id && dataToEdit.id !== 0
                ? 'LLM has been updated!'
                : 'LLM has been created!',
            );
          } else if (res.status === 401) {
            setLogout(true);
          }
        })
        .catch((err) => {
          makeToast(
            'error',
            err?.response?.data?.message
              ? `${err?.response?.data?.message}.`
              : 'Something went wrong!',
          );
        });
    }
  };

  const handleCancelClick = () => {
    setLLMDetails(emptyFormState(defaultProviderId));
    setOpen(false);
    setErrorsMsgs({
      name: '',
      input_price_per_million: '',
      output_price_per_million: '',
      model_score: '',
    });
  };

  const boolField = (name: keyof llmDetailsProps, label: string) => {
    const inputId = `llm-bool-${String(name)}`;
    return (
      <div key={String(name)} className="flex items-center gap-2 text-[#344054]">
        <input
          id={inputId}
          type="checkbox"
          name={String(name)}
          checked={Boolean(llmDetails[name])}
          onChange={handleDataChange}
          className="border-[#101828] border-[2px] w-[15px] h-[15px]"
        />
        <label htmlFor={inputId} className="text-sm cursor-pointer">
          {label}
        </label>
      </div>
    );
  };

  return (
    <BasicModal
      className="w-[720px] max-w-[95vw]"
      open={open}
      modalTitle={modalTitle}
      modalDesc={modalDesc}
      modalLogo={LLMIcon}
      saveData={saveLLM}
      resetData={handleCancelClick}
      confirmButtonText="Save"
    >
      <div className="max-h-[62vh] overflow-y-auto pr-1 flex flex-col gap-3">
        <div className="flex flex-col text-sm w-full">
          <span className="text-[#344054] font-medium">
            {AddProviderText}
            <span className="text-red-600">*</span>
          </span>
          <FilterDropdown
            options={
              providers?.map((item) => ({ value: String(item.id), label: item.provider_name })) ??
              []
            }
            value={String(llmDetails?.provider_id ?? providers?.[0]?.id ?? '')}
            onChange={(v: string) =>
              handleDataChange({
                target: { name: 'provider_id', value: v },
              } as React.ChangeEvent<HTMLSelectElement>)
            }
            placeholder="Select provider"
            showClear={false}
            className="mt-1 border border-[#E4E4E7] rounded-lg w-full px-3 py-2 text-left flex items-center justify-between"
          />
        </div>
        <div className="flex flex-col bg-[#E0E6F6] p-[15px] rounded-lg gap-3">
          <span className="font-bold text-base">{`${
            llmDetails?.provider_id !== 0
              ? providers?.filter((item) => item.id === Number(llmDetails?.provider_id))?.[0]
                  ?.provider_name
              : providers?.[0]?.provider_name
          } (${ProviderText})`}</span>
          <div className="flex flex-col text-sm">
            <span className="text-[#344054] font-medium">
              {ModelNameText}
              <span className="text-red-600">*</span>
            </span>
            <input
              placeholder="Enter model name"
              className={`mt-1 ${
                errorsMsgs.name !== '' && 'error-msg focus:border-red-600 border-red-600'
              } border border-[#E4E4E7] rounded-lg`}
              value={llmDetails?.name}
              name="name"
              onChange={handleDataChange}
            />
            {errorsMsgs.name && <span className="text-red-600">{errorsMsgs.name}</span>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col text-sm w-full">
              <span className="text-[#344054] font-medium">
                {PromptCostText}
                <span className="text-red-600">*</span>
              </span>
              <input
                type="number"
                placeholder="Input / 1M tokens"
                className={`mt-1 ${
                  errorsMsgs.input_price_per_million !== '' &&
                  'error-msg focus:border-red-600 border-red-600'
                } border border-[#E4E4E7] rounded-lg`}
                value={llmDetails?.input_price_per_million ?? ''}
                name="input_price_per_million"
                onChange={handleDataChange}
                min={0}
                step="any"
              />
              {errorsMsgs.input_price_per_million && (
                <span className="text-red-600">{errorsMsgs.input_price_per_million}</span>
              )}
            </div>
            <div className="flex flex-col text-sm w-full">
              <span className="text-[#344054] font-medium">
                {ResponseCostText}
                <span className="text-red-600">*</span>
              </span>
              <input
                type="number"
                placeholder="Output / 1M tokens"
                className={`mt-1 ${
                  errorsMsgs.output_price_per_million !== '' &&
                  'error-msg focus:border-red-600 border-red-600'
                } border border-[#E4E4E7] rounded-lg`}
                value={llmDetails?.output_price_per_million ?? ''}
                name="output_price_per_million"
                onChange={handleDataChange}
                min={0}
                step="any"
              />
              {errorsMsgs.output_price_per_million && (
                <span className="text-red-600">{errorsMsgs.output_price_per_million}</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Cached input price (per 1M)</span>
              <input
                type="number"
                placeholder="Optional"
                className="mt-1 border border-[#E4E4E7] rounded-lg"
                value={llmDetails.cached_input_price_per_million ?? ''}
                name="cached_input_price_per_million"
                onChange={handleDataChange}
                min={0}
                step="any"
              />
            </div>
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Currency</span>
              <input
                placeholder="USD"
                className="mt-1 border border-[#E4E4E7] rounded-lg"
                value={llmDetails.currency ?? 'USD'}
                name="currency"
                onChange={handleDataChange}
              />
            </div>
          </div>

          <div className="flex flex-col text-sm">
            <span className="text-[#344054] font-medium">{DescriptionText}</span>
            <textarea
              placeholder="Enter description"
              className="mt-1 border border-[#E4E4E7] rounded-lg min-h-[72px]"
              value={llmDetails?.description}
              name="description"
              onChange={handleDataChange}
            />
          </div>
        </div>

        <div className="flex flex-col bg-[#F4F7FB] p-[15px] rounded-lg gap-3 border border-[#E4E4E7]">
          <span className="font-semibold text-[#344054]">Routing &amp; scoring</span>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Model score (0–1)</span>
              <input
                type="number"
                step="0.001"
                min={0}
                max={1}
                placeholder="e.g. 0.86"
                className={`mt-1 border border-[#E4E4E7] rounded-lg ${
                  errorsMsgs.model_score ? 'border-red-600' : ''
                }`}
                value={llmDetails.model_score ?? ''}
                name="model_score"
                onChange={handleDataChange}
              />
              {errorsMsgs.model_score && (
                <span className="text-red-600">{errorsMsgs.model_score}</span>
              )}
            </div>
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Routing band (1–5)</span>
              <div
                className="mt-1 border border-[#E4E4E7] rounded-lg px-3 py-2 bg-[#F9FAFB] text-[#475467]"
                title="Computed from model score in the database"
              >
                {previewRoutingBand != null ? (
                  <span className="font-semibold text-[#132961]">Band {previewRoutingBand}</span>
                ) : (
                  'Set model score — band is auto-calculated'
                )}
              </div>
              <span className="text-[10px] text-[#71717A] mt-1">
                Bands: &lt;0.2→1, &lt;0.4→2, &lt;0.6→3, &lt;0.8→4, else 5
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Model type</span>
              <select
                name="model_type"
                className="mt-1 border border-[#E4E4E7] rounded-lg px-2 py-2"
                value={llmDetails.model_type ?? 'chat'}
                onChange={handleDataChange}
              >
                {MODEL_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Model status</span>
              <select
                name="model_status"
                className="mt-1 border border-[#E4E4E7] rounded-lg px-2 py-2"
                value={llmDetails.model_status ?? 'testing'}
                onChange={handleDataChange}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-col bg-[#F4F7FB] p-[15px] rounded-lg gap-3 border border-[#E4E4E7]">
          <span className="font-semibold text-[#344054]">Capabilities &amp; limits</span>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Input modalities</span>
              <input
                placeholder="text,image"
                className="mt-1 border border-[#E4E4E7] rounded-lg"
                value={llmDetails.input_modalities ?? ''}
                name="input_modalities"
                onChange={handleDataChange}
              />
            </div>
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Output modalities</span>
              <input
                placeholder="text"
                className="mt-1 border border-[#E4E4E7] rounded-lg"
                value={llmDetails.output_modalities ?? ''}
                name="output_modalities"
                onChange={handleDataChange}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Context window (tokens)</span>
              <input
                type="number"
                min={0}
                className="mt-1 border border-[#E4E4E7] rounded-lg"
                value={llmDetails.context_window_tokens ?? ''}
                name="context_window_tokens"
                onChange={handleDataChange}
              />
            </div>
            <div className="flex flex-col text-sm">
              <span className="text-[#344054] font-medium">Max output tokens</span>
              <input
                type="number"
                min={0}
                className="mt-1 border border-[#E4E4E7] rounded-lg"
                value={llmDetails.max_output_tokens ?? ''}
                name="max_output_tokens"
                onChange={handleDataChange}
              />
            </div>
          </div>
          <div className="flex flex-col text-sm">
            <span className="text-[#344054] font-medium mb-1">Health status</span>
            <select
              name="health_status"
              className="border border-[#E4E4E7] rounded-lg px-2 py-2"
              value={llmDetails.health_status ?? 'unknown'}
              onChange={handleDataChange}
            >
              {HEALTH_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {boolField('supports_tools', 'Supports tools')}
            {boolField('supports_json', 'Supports JSON')}
            {boolField('supports_streaming', 'Supports streaming')}
            {boolField('supports_reasoning', 'Supports reasoning')}
            {boolField('supports_native_files', 'Supports native files')}
            {boolField('approved_for_sensitive_data', 'Approved for sensitive data')}
          </div>
        </div>
      </div>
      {logout && createPortal(<Logout />, document.body)}
    </BasicModal>
  );
}

export default CreateOrUpdateLLM;
